import React, { useEffect, useState } from "react";
import { Modal, Button, Container, Row, Col, Card, Form } from "react-bootstrap";
import { CircularProgress } from "@material-ui/core";
import { useSelector } from "react-redux";
import { UserRole } from "../../../../constants/enum/UserRole";
import { useQuestions } from "../../../../hooks/questions/useQuestions";
import { QuestionDetails } from "../../../../hooks/questions/types/types";
import { Tag } from "../../../../hooks/tags/types/types";
import { FaRegHeart } from "react-icons/fa";
import SaveToListModal from "../../../../component/QuestionLists/SaveToListModal";
import { api } from "../../../../services/apis/configs/axiosConfigs";
import { API_URI } from "../../../../constants/consts";
import {
	mapEducationLevel,
	mapDifficultyLevel,
} from "../../../../utils/questions/questionsMap";
import { useSanitizeInput } from "../../../../constants/configJodit";

type ParsedJustificationSections = {
	baseHtml: string;
	wrongAlternativesHtml: string;
	alternativeStrategiesHtml: string;
};

type AiAlternative = {
	letra: string;
	texto: string;
};

type AiProcessedQuestion = {
	enunciado: string;
	alternativas: AiAlternative[];
	figura_url?: string | null;
	figura_base64?: string | null;
	figura_status?: string;
	modo?: string;
};

const UNESP_PDF_PATHS: Record<number, string> = {
	2022: "/var/www/html/data/pdfs/inep_enem/UNESP2022_1fase_prova.pdf",
	2023: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2023_1fase_prova.pdf",
	2024: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2024_2_1fase_prova.pdf",
	2025: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2025_2_1fase_prova.pdf",
	2026: "/var/www/html/data/provas_faltantes_2023_2026/UNESP2026_1fase_prova.pdf",
};

type ModalManageProps = {
	id: string | null;
	token: string;
	showModalManage: boolean;
	setShowModalManage: () => void;
};

const ModalManage: React.FC<ModalManageProps> = ({
	id,
	token,
	showModalManage,
	setShowModalManage,
}) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [questionData, setQuestionData] = useState<QuestionDetails | null>(
		null
	);
	const [error, setError] = useState<string>("");
	const [aiLoading, setAiLoading] = useState<boolean>(false);
	const [aiError, setAiError] = useState<string>("");
	const [aiData, setAiData] = useState<AiProcessedQuestion | null>(null);
	const [showReport, setShowReport] = useState<boolean>(false);
	const [reportText, setReportText] = useState<string>("");
	const [reportSubmitted, setReportSubmitted] = useState<boolean>(false);
	const [reportSubmitting, setReportSubmitting] = useState<boolean>(false);
	const [reportError, setReportError] = useState<string>("");
	const [showSaveToList, setShowSaveToList] = useState<boolean>(false);
	const [selectedObjectives, setSelectedObjectives] = useState<Record<string, string>>({});
	const [revealedCorrections, setRevealedCorrections] = useState<Record<string, boolean>>({});
	const [viewMode, setViewMode] = useState<"transcrita" | "foto">("transcrita");
	const [photoLoading, setPhotoLoading] = useState<boolean>(false);
	const [photoBase64, setPhotoBase64] = useState<string | null>(null);
	const [photoUrl, setPhotoUrl] = useState<string | null>(null);
	const [photoError, setPhotoError] = useState<string>("");
	const [transcribedAiData, setTranscribedAiData] = useState<AiProcessedQuestion | null>(null);
	const [transcribedAiLoading, setTranscribedAiLoading] = useState<boolean>(false);
	const [transcribedAiError, setTranscribedAiError] = useState<string>("");
	const { sanitizeInput } = useSanitizeInput();
	const { getQuestionById } = useQuestions(token);

	// Modo revisão: professor/admin/colaborador veem a questão completa (correta +
	// justificativa) sem precisar responder. Apenas alunos respondem.
	const role = useSelector((state: any) => state.auth.me?.role?.slug);
	const reviewMode = role !== UserRole.STUDENT_USER;

	// Listas de questões são recurso de ADMIN/PROFESSOR, igual ao coração da listagem.
	const canUseLists =
		role === UserRole.ADMIN_USER || role === UserRole.TEACHER_USER;

	const normalizeObjectiveText = (rawText?: string | null): string => {
		const wrapper = document.createElement("div");
		wrapper.innerHTML = String(rawText || "");
		let text = (wrapper.textContent || "").replace(/\s+/g, " ").trim();

		// Remove prefixos duplicados como "a) A)" ou "A. a-".
		text = text.replace(/^((?:[A-Ea-e])\s*[\)\.\-:]\s*)+/, "");

		// Remove sufixos de metadados acoplados na extração (ex.: "VNSP2504 | ...").
		text = text.replace(/\s+[A-Z]{3,}\d{3,}\s*\|.*$/g, "");

		return text.trim();
	};

	const getAlternativeLabel = (index: number): string => {
		return `${String.fromCharCode(65 + index)})`;
	};

	const handleObjectiveSelection = (subQuestionId: string, objectiveId: string) => {
		setSelectedObjectives((prev) => ({
			...prev,
			[subQuestionId]: objectiveId,
		}));

		// Ao trocar a alternativa, esconde a correção para exigir novo envio.
		setRevealedCorrections((prev) => ({
			...prev,
			[subQuestionId]: false,
		}));
	};

	const handleRevealCorrection = (subQuestionId: string) => {
		if (!selectedObjectives[subQuestionId]) return;

		setRevealedCorrections((prev) => ({
			...prev,
			[subQuestionId]: true,
		}));
	};

	const extractFirstImageSrc = (html?: string | null): string | null => {
		if (!html) return null;

		const wrapper = document.createElement("div");
		wrapper.innerHTML = String(html);
		const firstImage = wrapper.querySelector("img");

		return firstImage?.getAttribute("src") || null;
	};

	const removeImagesFromHtml = (html?: string | null): string => {
		if (!html) return "";

		const wrapper = document.createElement("div");
		wrapper.innerHTML = String(html);
		wrapper.querySelectorAll("img").forEach((image) => image.remove());

		return wrapper.innerHTML;
	};

	const normalizeLegacyQuestionHtml = (html?: string | null): string => {
		if (!html) return "";

		const wrapper = document.createElement("div");
		wrapper.innerHTML = String(html);

		// Remove elementos potencialmente problemáticos e libera conteúdo de colapsáveis legados.
		wrapper.querySelectorAll("script, style, iframe, object, embed").forEach((node) => node.remove());
		wrapper.querySelectorAll(".collapse").forEach((node) => {
			node.classList.remove("collapse");
			node.classList.add("show", "in");
			(node as HTMLElement).style.display = "block";
		});

		const textPreview = (wrapper.textContent || "").replace(/\s+/g, " ").trim();
		if (!textPreview) {
			return "";
		}

		return wrapper.innerHTML;
	};

	const normalizeImageUrl = (imageSrc: string): string => {
		if (imageSrc.startsWith("http://") || imageSrc.startsWith("https://")) {
			return imageSrc;
		}

		if (imageSrc.startsWith("//")) {
			return `${window.location.protocol}${imageSrc}`;
		}

		const apiOrigin = API_URI ? new URL(API_URI).origin : window.location.origin;
		return new URL(imageSrc, apiOrigin).toString();
	};

	const getCandidateImageSrc = (): string | null => {
		if (!questionData) return null;

		const fromQuestionStatement = extractFirstImageSrc(
			String(questionData.statement || "")
		);
		if (fromQuestionStatement) return fromQuestionStatement;

		const fromSubQuestion = extractFirstImageSrc(
			String(questionData.subQuestions?.[0]?.statement || "")
		);

		return fromSubQuestion;
	};

	const getNormalizedStatementHtml = (html?: string | null): string => {
		const withoutImages = removeImagesFromHtml(String(html || ""));
		return normalizeLegacyQuestionHtml(withoutImages);
	};

	const hasVisibleText = (html?: string | null): boolean => {
		if (!html) return false;
		const wrapper = document.createElement("div");
		wrapper.innerHTML = String(html);
		const normalized = (wrapper.textContent || "")
			.replace(/\u00a0/g, " ")
			.replace(/\s+/g, "")
			.trim();
		return normalized.length > 0;
	};

	const getDisplayStatementHtml = (
		html?: string | null,
		fallback = "Nenhum enunciado disponível."
	): string => {
		const normalized = getNormalizedStatementHtml(html);
		if (!hasVisibleText(normalized)) {
			return fallback;
		}

		const sanitized = sanitizeInput(normalized);
		return hasVisibleText(sanitized) ? sanitized : fallback;
	};

	const fetchImageViaBackend = async (imageUrl: string) => {
		const response = await api.request({
			url: `${API_URI}/admin/private/questions/process-image`,
			method: "POST",
			headers: { Authorization: `Bearer ${token}` },
			data: { image_url: imageUrl },
		});

		const result = response.data?.data ?? response.data;
		return {
			url: result?.figura_url ? normalizeImageUrl(result.figura_url) : null,
			base64: result?.figura_base64 ?? null,
		};
	};

	const getPdfExtractionParams = () => {
		if (questionData?.source !== "unesp-pdf-extractor" || !questionData.externalQuestionId) {
			return null;
		}

		const match = questionData.externalQuestionId.match(/unesp-1fase-(\d{4})-q(\d+)/);
		if (!match) return null;

		const year = parseInt(match[1], 10);
		const questionNumber = parseInt(match[2], 10);
		const pdfPath = UNESP_PDF_PATHS[year];
		if (!pdfPath) return null;

		return { pdfPath, questionNumber };
	};

	const fetchPdfProcessedQuestion = async () => {
		const params = getPdfExtractionParams();
		if (!params) return null;

		const response = await api.request({
			url: `${API_URI}/admin/private/questions/process-image`,
			method: "POST",
			headers: { Authorization: `Bearer ${token}` },
			data: {
				pdf_path: params.pdfPath,
				question_number: params.questionNumber,
			},
		});

		return response.data?.data ?? response.data;
	};

	const handleFetchQuestionImage = async () => {
		setAiError("");
		setAiData(null);

		// Questões UNESP extraídas de PDF: busca imagem do PDF original on-demand
		if (questionData?.source === "unesp-pdf-extractor" && questionData?.externalQuestionId) {
			const match = questionData.externalQuestionId.match(/unesp-1fase-(\d{4})-q(\d+)/);
			if (match) {
				const year = parseInt(match[1], 10);
				const questionNumber = parseInt(match[2], 10);
				const pdfPath = UNESP_PDF_PATHS[year];
				if (!pdfPath) {
					setAiError(`PDF da UNESP ${year} não está disponível para exibição de imagem.`);
					return;
				}
				setAiLoading(true);
				try {
					const response = await api.request({
						url: `${API_URI}/admin/private/questions/process-image`,
						method: "POST",
						headers: { Authorization: `Bearer ${token}` },
						data: { pdf_path: pdfPath, question_number: questionNumber },
					});
					const result = response.data?.data ?? response.data;
					if (result?.figura_base64 || result?.figura_url) {
						setAiData({
							enunciado: "",
							alternativas: [],
							figura_url: result.figura_url ? normalizeImageUrl(result.figura_url) : null,
							figura_base64: result.figura_base64 ?? null,
							figura_status: "extraída",
							modo: "image",
						});
					} else {
						setAiError("Imagem não encontrada para esta questão no PDF.");
					}
				} catch {
					setAiError("Erro ao carregar imagem do PDF. Tente novamente.");
				} finally {
					setAiLoading(false);
				}
				return;
			}
		}

		// Fallback: procura <img> no statement da questão ou sub-questão
		const imageSrc = getCandidateImageSrc();
		if (!imageSrc) {
			setAiError("Nenhuma imagem encontrada na questão para exibir.");
			return;
		}
		if (imageSrc.startsWith("data:image/")) {
			const parts = imageSrc.split(",");
			setAiData({
				enunciado: "",
				alternativas: [],
				figura_url: null,
				figura_base64: parts[1] ?? null,
				figura_status: "extraída",
				modo: "image",
			});
		} else {
			try {
				const normalized = normalizeImageUrl(imageSrc);
				const processed = await fetchImageViaBackend(normalized);
				if (processed.base64 || processed.url) {
					setAiData({
						enunciado: "",
						alternativas: [],
						figura_url: processed.url,
						figura_base64: processed.base64,
						figura_status: "extraída",
						modo: "image",
					});
				} else {
					setAiError("Imagem não encontrada para esta questão.");
				}
			} catch {
				setAiError("Erro ao carregar imagem da URL original da questão.");
			}
		}
	};

	const handleSubmitReport = async () => {
		if (!reportText.trim() || !questionData || reportSubmitting) return;

		setReportSubmitting(true);
		setReportError("");
		try {
			await api.request({
				url: `${API_URI}/admin/private/questions/${questionData.id}/report`,
				method: "POST",
				headers: { Authorization: `Bearer ${token}` },
				data: { description: reportText },
			});
			setReportSubmitted(true);
			setReportText("");
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.error?.message ||
				"Não foi possível enviar o report. Tente novamente.";
			setReportError(errorMessage);
			setReportSubmitted(false);
		} finally {
			setReportSubmitting(false);
		}
	};

	const parseJustificationSections = (
		html: string | null | undefined
	): ParsedJustificationSections => {
		const rawHtml = String(html || "").trim();
		if (!rawHtml) {
			return {
				baseHtml: "",
				wrongAlternativesHtml: "",
				alternativeStrategiesHtml: "",
			};
		}

		const wrapper = document.createElement("div");
		wrapper.innerHTML = rawHtml;

		let wrongAlternativesHtml = "";
		let alternativeStrategiesHtml = "";

		const headings = Array.from(wrapper.querySelectorAll("h4"));
		headings.forEach((heading) => {
			const title = (heading.textContent || "").trim().toLowerCase();
			const nextElement = heading.nextElementSibling;

			if (title.includes("alternativas incorretas")) {
				wrongAlternativesHtml = nextElement?.outerHTML || "";
				heading.remove();
				nextElement?.remove();
				return;
			}

			if (title.includes("outras formas de resolver")) {
				alternativeStrategiesHtml = nextElement?.outerHTML || "";
				heading.remove();
				nextElement?.remove();
			}
		});

		return {
			baseHtml: wrapper.innerHTML.trim(),
			wrongAlternativesHtml,
			alternativeStrategiesHtml,
		};
	};

	useEffect(() => {
		const fetchQuestion = async () => {
			if (!id || !showModalManage) return;
			setAiLoading(false);
			setAiError("");
			setAiData(null);
			setShowReport(false);
			setReportText("");
			setReportSubmitted(false);
			setReportSubmitting(false);
			setReportError("");
			setSelectedObjectives({});
			setRevealedCorrections({});
			setViewMode("transcrita");
			setPhotoBase64(null);
			setPhotoUrl(null);
			setPhotoError("");
			setTranscribedAiData(null);
			setTranscribedAiError("");
			setTranscribedAiLoading(false);
			setLoading(true);
			setError("");
			try {
				const question = await getQuestionById(id);
				setQuestionData(question);
			} catch (err) {
				setError("Falha ao carregar os dados da questão");
			} finally {
				setLoading(false);
			}
		};
		fetchQuestion();
	}, [id, showModalManage, token, getQuestionById]);

	useEffect(() => {
		if (!showModalManage) {
			setAiLoading(false);
			setAiError("");
			setAiData(null);
			setReportSubmitting(false);
			setReportError("");
			setSelectedObjectives({});
			setRevealedCorrections({});
			setViewMode("transcrita");
			setPhotoBase64(null);
			setPhotoUrl(null);
			setPhotoError("");
			setTranscribedAiData(null);
			setTranscribedAiError("");
			setTranscribedAiLoading(false);
		}
	}, [showModalManage]);

	useEffect(() => {
		const loadTranscribedFromPdf = async () => {
			if (!showModalManage || !questionData || transcribedAiLoading || transcribedAiData) {
				return;
			}

			if (questionData.source !== "unesp-pdf-extractor") {
				return;
			}

			const mainHasText = hasVisibleText(getNormalizedStatementHtml(String(questionData.statement || "")));
			const subHasText = Boolean(
				questionData.subQuestions?.some((sq) =>
					hasVisibleText(getNormalizedStatementHtml(String(sq.statement || "")))
				)
			);

			if (mainHasText || subHasText) {
				return;
			}

			if (!getPdfExtractionParams()) {
				return;
			}

			setTranscribedAiError("");
			setTranscribedAiLoading(true);
			try {
				const result = await fetchPdfProcessedQuestion();
				if (!result) {
					setTranscribedAiError("Não foi possível carregar a transcrição automática.");
					return;
				}

				const enunciado = String(result.enunciado || "").trim();
				const alternativas = Array.isArray(result.alternativas) ? result.alternativas : [];
				if (enunciado || alternativas.length > 0) {
					setTranscribedAiData({
						enunciado,
						alternativas,
						figura_base64: result.figura_base64 ?? null,
						figura_url: result.figura_url ?? null,
						figura_status: result.figura_status ?? "sem_imagem",
						modo: result.modo ?? "pdf",
					});
				}
			} catch {
				setTranscribedAiError("Falha ao gerar transcrição automática desta questão.");
			} finally {
				setTranscribedAiLoading(false);
			}
		};

		loadTranscribedFromPdf();
	}, [questionData, showModalManage, transcribedAiData, transcribedAiLoading]);

	const handleSwitchToFoto = async () => {
		if (photoBase64 || photoUrl) {
			setViewMode("foto");
			return;
		}
		setPhotoError("");
		setPhotoLoading(true);

		// Tentar buscar imagem do PDF (unesp-pdf-extractor)
		if (questionData?.source === "unesp-pdf-extractor" && questionData?.externalQuestionId) {
			const params = getPdfExtractionParams();
			if (params) {
				try {
					const result = await fetchPdfProcessedQuestion();
					const url = result?.figura_url ?? null;
					const b64 = result?.figura_base64 ?? null;
					if (b64) {
						setPhotoBase64(b64);
						setViewMode("foto");
					} else if (url) {
						setPhotoUrl(normalizeImageUrl(url));
						setViewMode("foto");
					} else {
						setPhotoError("Imagem não encontrada para esta questão no PDF.");
					}
				} catch {
					setPhotoError("Erro ao carregar imagem do PDF.");
				} finally {
					setPhotoLoading(false);
				}
				return;
			} else {
				setPhotoError("PDF da UNESP não disponível para esta questão.");
				setPhotoLoading(false);
				return;
			}
		}

		// Fallback: imagem embutida no statement
		const imgSrc = getCandidateImageSrc();
		if (imgSrc) {
			if (imgSrc.startsWith("data:image/")) {
				const b64 = imgSrc.split(",")[1];
				setPhotoBase64(b64);
				setViewMode("foto");
			} else {
				try {
					const normalized = normalizeImageUrl(imgSrc);
					const processed = await fetchImageViaBackend(normalized);
					if (processed.base64) {
						setPhotoBase64(processed.base64);
						setViewMode("foto");
					} else if (processed.url) {
						setPhotoUrl(processed.url);
						setViewMode("foto");
					} else {
						setPhotoError("Imagem não disponível para esta questão.");
					}
				} catch {
					setPhotoError("Erro ao carregar imagem da URL original da questão.");
				}
			}
		} else {
			setPhotoError("Nenhuma imagem disponível para esta questão.");
		}
		setPhotoLoading(false);
	};

	const renderViewToggle = () => (
		<div className="d-flex gap-2 mb-3">
			<Button
				variant={viewMode === "transcrita" ? "primary" : "outline-primary"}
				size="sm"
				onClick={() => setViewMode("transcrita")}
			>
				Questão Transcrita
			</Button>
			<Button
				variant={viewMode === "foto" ? "primary" : "outline-primary"}
				size="sm"
				disabled={photoLoading}
				onClick={handleSwitchToFoto}
			>
				{photoLoading ? "Carregando..." : "Ver como Foto"}
			</Button>
			{photoError && (
				<span className="text-danger" style={{ fontSize: "0.85rem", alignSelf: "center" }}>
					{photoError}
				</span>
			)}
		</div>
	);

	const questionStatementNormalized = getNormalizedStatementHtml(
		String(questionData?.statement || "")
	);

	const shouldRenderQuestionStatement = hasVisibleText(questionStatementNormalized);

	const renderSubQuestions = () => {
		if (!questionData?.subQuestions) return null;
		const mainStatementDisplayHtml = getDisplayStatementHtml(String(questionData.statement || ""), "");
		const aiStatementDisplayHtml = transcribedAiData?.enunciado
			? sanitizeInput(transcribedAiData.enunciado)
			: "";
		return questionData.subQuestions.map((subQuestion, index) => (
			<Col xs={12} key={subQuestion.id}>
				{(() => {
					const subStatementDisplayHtml = getDisplayStatementHtml(String(subQuestion.statement || ""), "");
					const mergedStatementDisplayHtml =
						subStatementDisplayHtml ||
						(questionData.subQuestions?.length === 1 ? mainStatementDisplayHtml : "") ||
						aiStatementDisplayHtml ||
						"Nenhum enunciado disponível.";

					const subQuestionId = String(subQuestion.id);
					const isObjective = subQuestion.type.toLowerCase() === "objective";
					const selectedObjectiveId = selectedObjectives[subQuestionId] || "";
					const isCorrectionVisible =
						reviewMode || !isObjective || Boolean(revealedCorrections[subQuestionId]);

					const selectedObjective =
						isObjective && subQuestion.objectives
							? subQuestion.objectives.find(
								(obj) => String(obj.id) === selectedObjectiveId
							)
							: null;

					const selectedIsCorrect = Boolean(selectedObjective?.isCorrect);

					const parsedJustification = parseJustificationSections(
						String(subQuestion.answerJustification || "")
					);

					return (
						<Card className="mb-3 border-0">
							<Card.Body>
								<Card.Title className="mb-3" style={{ fontSize: "1.1rem" }}>
									{`Questão ${index + 1}`}
								</Card.Title>
								{viewMode !== "foto" && (
									<div>
										<div
											className="question-rich-content"
											dangerouslySetInnerHTML={{
												__html: mergedStatementDisplayHtml,
											}}
										/>
									</div>
								)}
								{viewMode !== "foto" && transcribedAiLoading && !hasVisibleText(mergedStatementDisplayHtml) && (
									<div className="text-muted mt-2" style={{ fontSize: "0.9rem" }}>
										Gerando transcrição automática...
									</div>
								)}
								{viewMode !== "foto" && transcribedAiError && !hasVisibleText(mergedStatementDisplayHtml) && (
									<div className="text-danger mt-2" style={{ fontSize: "0.9rem" }}>
										{transcribedAiError}
									</div>
								)}
								{isObjective &&
									subQuestion.objectives && (
										<>
											{viewMode !== "foto" && <hr style={{ opacity: 0.5 }} />}
											<div style={{ fontWeight: 500, marginBottom: "0.5rem" }}>
												Alternativas
											</div>
											{subQuestion.objectives.map((obj, idx) => (
												<div
													key={obj.id}
													style={{
														padding: "0.5rem",
														marginBottom: "0.5rem",
														borderRadius: "0.25rem",
														backgroundColor: isCorrectionVisible
															? obj.isCorrect
																? "rgba(40,167,69,0.18)"
																: selectedObjectiveId === String(obj.id)
																	? "rgba(220,53,69,0.16)"
																	: "#f1f1f1"
															: selectedObjectiveId === String(obj.id)
																? "rgba(13,110,253,0.12)"
																: "#f1f1f1",
														border: selectedObjectiveId === String(obj.id)
															? "1px solid rgba(13,110,253,0.45)"
															: "1px solid transparent",
													}}
												>
													{reviewMode ? (
														<span style={{ fontSize: "0.95rem" }}>
															<strong>{getAlternativeLabel(idx)}</strong>{" "}
															{normalizeObjectiveText(String(obj.text) || "")}
															{obj.isCorrect && (
																<span className="badge bg-success ms-2">
																	✓ Correta
																</span>
															)}
														</span>
													) : (
														<Form.Check
															type="radio"
															name={`objective-${subQuestionId}`}
															id={`objective-${subQuestionId}-${obj.id}`}
															checked={selectedObjectiveId === String(obj.id)}
															onChange={() =>
																handleObjectiveSelection(subQuestionId, String(obj.id))
															}
															label={
																<span style={{ fontSize: "0.95rem" }}>
																	<strong>{getAlternativeLabel(idx)}</strong>{" "}
																	{normalizeObjectiveText(String(obj.text) || "")}
																</span>
															}
														/>
													)}
												</div>
											))}
											{!reviewMode && (
												<div className="d-flex align-items-center gap-2 mt-2">
													<Button
														variant="primary"
														size="sm"
														disabled={!selectedObjectiveId}
														onClick={() => handleRevealCorrection(subQuestionId)}
													>
														Ver correção
													</Button>
													{isCorrectionVisible && selectedObjectiveId && (
														<div
															className={`alert ${selectedIsCorrect ? "alert-success" : "alert-danger"} mb-0 py-1 px-2`}
															style={{ fontSize: "0.9rem" }}
														>
															{selectedIsCorrect
																? "Resposta correta."
																: "Resposta incorreta. Veja a correção abaixo."}
														</div>
													)}
												</div>
											)}
										</>
									)}
								{isCorrectionVisible && (
									<>
										{(viewMode !== "foto" || isObjective) && <hr style={{ opacity: 0.5 }} />}
										<div style={{ fontWeight: 500, marginBottom: "0.5rem" }}>
											Justificativa da Resposta
										</div>
										<div>
											<div
												className="question-rich-content"
												dangerouslySetInnerHTML={{
													__html:
														sanitizeInput(parsedJustification.baseHtml) ||
														"Nenhuma justificativa disponível.",
												}}
											/>
										</div>
										{parsedJustification.wrongAlternativesHtml && (
											<>
												<hr style={{ opacity: 0.5 }} />
												<div style={{ fontWeight: 500, marginBottom: "0.5rem" }}>
													Por que as alternativas erradas estão erradas
												</div>
												<div
													className="question-rich-content ai-enrichment-box"
													dangerouslySetInnerHTML={{
														__html: sanitizeInput(
															parsedJustification.wrongAlternativesHtml
														),
													}}
												/>
											</>
										)}
										{parsedJustification.alternativeStrategiesHtml && (
											<>
												<hr style={{ opacity: 0.5 }} />
												<div style={{ fontWeight: 500, marginBottom: "0.5rem" }}>
													Outras maneiras de resolver
												</div>
												<div
													className="question-rich-content ai-enrichment-box"
													dangerouslySetInnerHTML={{
														__html: sanitizeInput(
															parsedJustification.alternativeStrategiesHtml
														),
													}}
												/>
											</>
										)}
									</>
								)}
							</Card.Body>
						</Card>
					);
				})()}
			</Col>
		));
	};

	return (
		<>
			{/* Removendo o fundo preto do backdrop e liberando a interação */}
			<style type="text/css">
				{`
          .transparent-backdrop {
            background-color: transparent !important;
            pointer-events: none;
          }

          .question-rich-content img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 0.75rem 0;
            border-radius: 0.25rem;
          }

					.ai-enrichment-box {
						background: #f7fbff;
						border: 1px solid #d7e8f8;
						border-radius: 0.5rem;
						padding: 0.75rem 1rem;
					}

					.ai-enrichment-box ul {
						margin-bottom: 0;
						padding-left: 1.25rem;
					}
        `}
			</style>
			<Modal
				show={showModalManage}
				onHide={setShowModalManage}
				size="xl"
				centered
				dialogClassName="modal-dialog-centered modal-xl"
				backdropClassName="transparent-backdrop"
				onExited={() => {
					setQuestionData(null);
					setError("");
				}}
			>
				<Modal.Header closeButton>
					<Modal.Title>Gerenciar Questão</Modal.Title>
				</Modal.Header>
				<Modal.Body style={{ overflowY: "auto", maxHeight: "80vh" }}>
					<Container fluid>
						{loading ? (
							<div className="text-center py-4">
								<CircularProgress />
							</div>
						) : error ? (
							<div className="alert alert-danger" role="alert">
								{error}
							</div>
						) : questionData ? (
							<>
								<Row className="mb-3">
									<Col xs={12} className="d-flex justify-content-end gap-2">
										{canUseLists && (
											<Button
												id="btn-add-to-list-modal"
												type="button"
												variant="outline-danger"
												size="sm"
												onClick={() => setShowSaveToList(true)}
											>
												<FaRegHeart className="me-1" /> Adicionar à lista
											</Button>
										)}
										<Button
											variant={showReport ? "warning" : "outline-warning"}
											size="sm"
											onClick={() => {
												setShowReport(!showReport);
												setReportText("");
												setReportSubmitted(false);
												setReportSubmitting(false);
												setReportError("");
												setAiData(null);
												setAiError("");
											}}
										>
											⚑ Reportar Erro
										</Button>
									</Col>
								</Row>

								{showReport && (
									<Row className="mb-4">
										<Col xs={12}>
											<Card className="shadow-sm" style={{ borderColor: "#ffc107" }}>
												<Card.Header style={{ background: "#fff9e6", color: "#856404", fontWeight: 600 }}>
													⚑ Reportar Erro na Questão
												</Card.Header>
												<Card.Body>
													<Form.Group className="mb-3">
														<Form.Label style={{ fontWeight: 500 }}>Descreva o erro encontrado</Form.Label>
														<Form.Control
															as="textarea"
															rows={3}
															value={reportText}
															onChange={(e) => setReportText(e.target.value)}
															placeholder="Ex: texto incompleto, alternativa incorreta, imagem faltando..."
														/>
													</Form.Group>
													<div className="d-flex gap-2 flex-wrap align-items-center">
														<Button
															variant="outline-secondary"
															size="sm"
															disabled={aiLoading}
															onClick={handleFetchQuestionImage}
														>
															{aiLoading ? "Carregando imagem..." : "Ver questão como imagem"}
														</Button>
														<Button
															variant="warning"
															size="sm"
															disabled={!reportText.trim() || reportSubmitted || reportSubmitting}
															onClick={handleSubmitReport}
														>
															{reportSubmitting
																? "Enviando..."
																: reportSubmitted
																	? "✓ Report enviado"
																	: "Enviar Report"}
														</Button>
													</div>
													{reportError && (
														<div className="alert alert-danger mt-2 mb-0 py-2" role="alert">
															{reportError}
														</div>
													)}
													{aiError && (
														<div className="alert alert-danger mt-2 mb-0 py-2" role="alert">
															{aiError}
														</div>
													)}
													{(aiData?.figura_base64 || aiData?.figura_url) && (
														<div className="mt-3">
															<div style={{ fontWeight: 600, marginBottom: "0.4rem" }}>Questão em formato imagem (original PDF):</div>
															<img
																src={
																	aiData!.figura_base64
																		? `data:image/png;base64,${aiData!.figura_base64}`
																		: aiData!.figura_url!
																}
																alt="Questão em formato imagem"
																style={{
																	maxWidth: "100%",
																	height: "auto",
																	borderRadius: "0.25rem",
																	background: "#f8f9fa",
																}}
															/>
														</div>
													)}
												</Card.Body>
											</Card>
										</Col>
									</Row>
								)}


								<Row className="mb-4">
									<Col xs={12}>
										<Card className="shadow-sm border-0">
											<Card.Header>Detalhes da Questão</Card.Header>
											<Card.Body>
												<p className="mb-2">
													<strong>Nível de Educação:</strong>{" "}
													{mapEducationLevel(questionData.educationLevel || "")}
												</p>
												<p className="mb-2">
													<strong>Dificuldade:</strong>{" "}
													{mapDifficultyLevel(
														questionData.difficultyLevel || ""
													)}
												</p>
												<p className="mb-2">
													<strong>Disciplina:</strong>{" "}
													{questionData.disciplines.name}
												</p>
												{questionData.tags && questionData.tags.length > 0 && (
													<p className="mb-0">
														<strong>Tags:</strong>{" "}
														<span className="text-muted">
															{questionData.tags
																.map((tag: Tag) => tag.name)
																.join(" · ")}
														</span>
													</p>
												)}
											</Card.Body>
										</Card>
									</Col>
								</Row>
								<Row className="g-4">
									{shouldRenderQuestionStatement && viewMode !== "foto" && (
										<Col xs={12}>
											<Card className="shadow-sm border-0">
												<Card.Body>
													{questionData?.subQuestions && questionData.subQuestions.length > 1 && (
														<Card.Title>Enunciado da Questão</Card.Title>
													)}
													<div>
														<div
															className="question-rich-content"
															dangerouslySetInnerHTML={{
																__html: getDisplayStatementHtml(String(questionData.statement)),
															}}
														/>
													</div>
												</Card.Body>
											</Card>
										</Col>
									)}
									<Col xs={12}>
										{renderViewToggle()}
									</Col>
									{viewMode === "foto" ? (
										<Col xs={12}>
											{(photoBase64 || photoUrl) ? (
												<Card className="shadow-sm border-0">
													<Card.Body>
														<img
															src={photoUrl ?? `data:image/png;base64,${photoBase64}`}
															alt="Questão em formato PDF"
															style={{ maxWidth: "100%", height: "auto", borderRadius: "0.25rem" }}
														/>
													</Card.Body>
												</Card>
											) : photoLoading ? (
												<div className="text-center py-4 text-muted">Carregando imagem...</div>
											) : null}
										</Col>
									) : null}
									{renderSubQuestions()}
								</Row>
							</>
						) : (
							<div className="alert alert-info">Nenhum dado para exibir.</div>
						)}
					</Container>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="outline-secondary"
						size="lg"
						onClick={setShowModalManage}
					>
						Fechar
					</Button>
				</Modal.Footer>
			</Modal>

			{canUseLists && (
				<SaveToListModal
					show={showSaveToList}
					onHide={() => setShowSaveToList(false)}
					questionId={questionData?.id ?? null}
					token={token}
				/>
			)}
		</>
	);
};

export default ModalManage;
